const Vue = require('vue');
const Q = require('q');
const Utils = require('utilities');
const AbortionError = require('core/errors/AbortionError');
const abortify = require('async/abortify');
const logger = require('utilities/logger');

/**
 * prepares a key for use in the Vue store (no leading non-alpha, no '-', basically JS variable rules)
 *
 * @param {String} key
 * @returns {String} a key for use in cache store
 */
function mkKey(key) {
	// using a leading 'k' for "key" because _ at the beginning wouldn't
	// work (apparently a Vue quirk)
	return 'k' + key.replace(/[^a-z0-9]/gi, '_');
}

function _assign(store, key, target, source) {
	//if array, overwrite.
	if (target instanceof Array || source instanceof Array) {
		Vue.set(store, key, source);
	} else {
		//Else merge
		Object.getOwnPropertyNames(source).forEach(function (k) {
			Vue.set(target, k, Utils.cloneDeep(source[k]));
		});

		Object.getOwnPropertyNames(target).forEach(function (k) {
			// remove properties that don't exist on new value,
			// but skip those starting with _, particularly __ob__
			// since removing that property broke Vue's binding
			if (!k.match(/^_/) && !source.hasOwnProperty(k)) {
				Vue.delete(target, k);
			}
		});
	}
}

function Cache() {
	this._registry = {};
	this._store = new Vue();
}

Cache.prototype = {
	/**
	 * Registers a cache initializing the metadata needed to track this cache
	 *
	 * @param  {String} name       name of the cache
	 */
	register: function (name) {
		if (this._registry[name]) {
			throw new Error('A cache with this name is already registered');
		}

		var cache = (this._registry[name] = {
			name: name,
			keysSet: {},
			deferreds: {},
			storeName: mkKey(name)
		});

		this._store[cache.storeName] = {};

		cache.store = this._store[cache.storeName];
	},
	_getCache: function (cacheName) {
		if (!this._registry[cacheName]) {
			this.register(cacheName, []);
		}
		return this._registry[cacheName];
	},
	/**
	 * Synchronously sets a cache key
	 *
	 * @param {String} cacheName The name of the cache passed to register
	 * @param {String} key       The key
	 * @param {Object} value     The object to cache
	 */
	set: function (cacheName, key, value) {
		if (typeof value === 'undefined') {
			throw new Error('Cache does not support "undefined" values');
		}

		var cache = this._getCache(cacheName);
		key = mkKey(key);

		cache.keysSet[key] = true;
		this._storeSet(cache, key, value);
	},
	_storeGet: function (cache, safeKey, raw) {
		var v = this._store[cache.storeName][safeKey];
		if (raw) {
			return v;
		}

		if (!v) {
			return undefined;
		} else {
			return Utils.cloneDeep(v);
		}
	},
	_storeSet: function (cache, safeKey, value) {
		value = value || {};

		if (cache.store[safeKey]) {
			var o = cache.store[safeKey];

			_assign(cache.store, safeKey, o, value);
		} else {
			Vue.set(cache.store, safeKey, Utils.cloneDeep(value));
		}
	},
	/**
	 * Get a key from the cache asynchronously with a promise.
	 *
	 * @param {String} cacheName    The name of the cache passed to register
	 * @param {String} key          The key
	 * @param  {Function} cb        A callback used to populate the cache on a cache miss. It can
	 *                              return a normal value or a promise
	 * @return {Object|Promise}     A promise that will resolve with the value from cache
	 *                              (or from the callback on cache miss)
	 */
	get: function (cacheName, key, cb, _force) {
		var cache = this._getCache(cacheName);

		var safeKey = mkKey(key);

		if (cache.keysSet[safeKey]) {
			// cache hit pre-resolve a promise
			return Q.resolve(this._storeGet(cache, safeKey));
		} else if (cache.deferreds[safeKey] && !_force) {
			var deferred = Q.defer();

			var l = cache.deferreds[safeKey].push(deferred);

			return abortify(deferred.promise).onAbort(function () {
				cache.deferreds[safeKey].slice(l, 1);
			});
		} else {
			// cache miss with a callback
			var cbReturn = cb();

			if (cbReturn && typeof cbReturn.then === 'function') {
				var self = this;

				var promise = cbReturn.then(
					function (v) {
						if (cache.deferreds[safeKey]) {
							cache.deferreds[safeKey].forEach(function (d) {
								d.resolve(v);
							});
						}

						this.set(cache.name, key, v);

						delete cache.deferreds[safeKey];

						return this._storeGet(cache, safeKey);
					}.bind(this),
					function (e) {
						if (e instanceof AbortionError) {
							if (cache.deferreds[safeKey] && cache.deferreds[safeKey].length) {
								// Manually call `get` again to force processing of pending deferreds.
								// Current aborted call will not be retried in any way.
								self.get(cacheName, key, cb, true);
							} else if (cache.deferreds[safeKey]) {
								delete cache.deferreds[safeKey];
							}
						} else {
							cache.deferreds[safeKey].forEach(function (d) {
								d.reject(e);
							});

							delete cache.deferreds[safeKey];
						}

						throw e;
					}
				);

				if (!cache.deferreds[safeKey]) {
					cache.deferreds[safeKey] = [];
				}

				return promise;
			} else {
				this.set(cache.name, key, cbReturn);
				return Q.resolve(this._storeGet(cache, safeKey));
			}
		}
	},
	/**
	 * Clears a key from the specified cache, any caches that depend on this cache will be
	 * completely cleared.
	 *
	 * @param  {String} cacheName the cache name registered
	 * @param  {String} key       the key to clear
	 */
	invalidate: function (cacheName, key, safeKey) {
		var cache = this._getCache(cacheName);
		if (safeKey) {
			key = safeKey;
		} else {
			key = mkKey(key);
		}

		delete cache.keysSet[key];

		var promises = [];

		if (promises.length === 0) {
			promises.push(Q());
			if (cache.store[key]) {
				this._storeSet(cache, key, null);
			}
		}

		return Q.all(promises);
	},

	/**
	 * Clears the specified cache, any caches that depend on this cache will also be
	 * completely cleared.
	 *
	 * @param  {String} cacheName the cache name registered
	 */
	invalidateFullCache: function (cacheName) {
		var cache = this._getCache(cacheName);
		var promises = [Q()];

		Object.keys(cache.store).forEach(
			function (k) {
				this.invalidate(cacheName, null, k);
			}.bind(this)
		);

		return Q.all(promises);
	},

	invalidateAllCaches: function () {
		Object.keys(this._registry).forEach(
			function (cacheName) {
				logger.info('clearing cache', cacheName);
				this.invalidateFullCache(cacheName);
			}.bind(this)
		);
	}
};

module.exports = new Cache();
